import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { ArrowRightIcon, DescriptionItem, Divider, Flex, Label, Text, Button } from 'src/components';
import { colors } from "src/theme";
import { formatNumber, i18n, ProductTypeCodeEnum, setPaymentType, useAppDispatch } from "src/common";
import { AppRoutes } from "src/pages/app";
import { sendGeneralTransferDetailsToEmail } from 'src/common/api/apollo/supplementary';


interface AccountPaymentDataCardProps {
  data: any;
  type?: string;
  offerId: string;
}

export const AccountPaymentDataCard = ({ data, type = 'oneTime', offerId }: AccountPaymentDataCardProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [sendGeneralTransferDetailsToEmailMutation] = useMutation(sendGeneralTransferDetailsToEmail);

  useEffect(() => {
    dispatch(setPaymentType(type));
  }, [type]);

  const handlePay = () => {
    if (type === 'oneTime') {
      sendGeneralTransferDetailsToEmailMutation({
        variables: { id: offerId, input: { type: ProductTypeCodeEnum.DOPUNSKO, installments: 1 } }
      });
      navigate(`${AppRoutes.CHECKOUT_PAYMENT_SENT.fullPath}/${offerId}`)
    }
    else {
      sendGeneralTransferDetailsToEmailMutation({
        variables: { id: offerId, input: { type: ProductTypeCodeEnum.DOPUNSKO, installments: 12 } }
      });
      navigate(`${AppRoutes.CHECKOUT_PAYMENT_SENT_INSTALLMENTS.fullPath}/${offerId}`)
    }
  }

  return (
    <Flex vertical gap={20}>
      {type === 'oneTime' && <><Label type='small'>{i18n.t('components.cards.accountPaymentDataCard.important')}</Label>
        <Flex gap={20}>
          <ArrowRightIcon color={colors.icon2} />
          <Text type='tiny'>{i18n.t('components.cards.accountPaymentDataCard.text')}</Text>
        </Flex>
      </>}
      {type === 'installments' &&
        <Flex gap={20}>
          <ArrowRightIcon color={colors.icon2} />
          <Text type='tiny'>{i18n.t('components.cards.accountPaymentDataCard.textInstallments')}</Text>
        </Flex>
      }
      <Divider dashed />
      <Flex vertical gap={12}>
        <Text type='base' bold>{i18n.t('components.cards.accountPaymentDataCard.paymentData')}</Text>
        <DescriptionItem label={i18n.t('components.cards.accountPaymentDataCard.payer')} value={`${data.customerFirstName} ${data.customerLastName}`} />
        <DescriptionItem label={i18n.t('components.cards.accountPaymentDataCard.receiver')} value={
          <Flex vertical>
            <Text type='base'>{data.recipientName}</Text>
            <Text type='base'>{data.recipientAddress}</Text>
            <Text type='base'>{`${data.recipientZipCode} ${data.recipientCity}`}</Text>
          </Flex>
        } />
        <Divider dashed />
        <DescriptionItem label={i18n.t('components.cards.accountPaymentDataCard.amount')} value={`${type === 'oneTime' ? formatNumber(data!.price) : formatNumber(data!.price / 12)} EUR`} />
        <Divider dashed />
        <DescriptionItem label={i18n.t('components.cards.accountPaymentDataCard.referenceNumber')} value={`${data.referencePrefix}${data.referenceNumberType === 'oib' ? data.referenceNumber : ''}${data.referenceSuffix}`} />
        <DescriptionItem label={i18n.t('components.cards.accountPaymentDataCard.model')} value={data.model} />
        <DescriptionItem label={i18n.t('components.cards.accountPaymentDataCard.description')} value={data.description} />
        <DescriptionItem label={i18n.t('components.cards.accountPaymentDataCard.iban')} value={data.recipientAccountNumber} />
      </Flex>
      <Divider dashed />
      <Text type='base' bold>{i18n.t('components.cards.accountPaymentDataCard.steps')}</Text>
      <Flex gap={20}>
        <ArrowRightIcon color={colors.icon2} />
        <Text type='tiny'>{i18n.t('components.cards.accountPaymentDataCard.step1')}</Text>
      </Flex>
      <Flex gap={20}>
        <ArrowRightIcon color={colors.icon2} />
        <Text type='tiny'>{i18n.t('components.cards.accountPaymentDataCard.step2')}</Text>
      </Flex>
      <Button btnType='primary' text={i18n.t('components.cards.accountPaymentDataCard.button')} onClick={handlePay} />
    </Flex>
  )
}
